import type { AccountConfigurationView } from '@/types/api/investment-advisor-representative'
import type { Amount } from '@/types/api/order'

interface Trade {
  amount: Amount
  price: number
  venue_mpid: string
  venue_execution_id: string
}

export interface AccountAllocation {
  account_id: string
  requested_amount: Amount
  actual_amount: Amount
  fill_price: number
  venue_mpid: string
  venue_execution_id: string
  accrued_interest?: number
  net_money?: number
  pre_fee_net_money?: number
  account_configuration?: AccountConfigurationView
}

export interface AccountRequest {
  account_id: string
  requested_amount: Amount
  account_configuration?: AccountConfigurationView
  // The Moment internal ID of the sleeve the order is allocated to. Present on
  // the runtime feed (Horizon #1751) but absent from the generated readme spec.
  moment_sleeve_id?: string
}

export interface AllocationSummary {
  id: string
  client_allocation_id: string
  client_order_id: string
  custodian_id: string
  ib_id: string
  risk_group_id: string
  order_id: string
  status: AllocationStatus
  strategy: string
  account_requests: AccountRequest[]
  account_allocations: AccountAllocation[]
  trades: Trade[]
  calculated_at: string | null
  submitted_at: string | null
}

export enum AllocationUpdateEventType {
  Create = 'allocation_create',
  Approve = 'allocation_approve',
  Calculate = 'allocation_calculate',
  Submit = 'allocation_submit',
  Fail = 'allocation_fail',
  Reject = 'allocation_reject',
  Cancel = 'allocation_cancel',
  Succeed = 'allocation_succeed',
}

export enum AllocationStatus {
  Approved = 'approved',
  Calculated = 'calculated',
  Canceled = 'canceled',
  Created = 'created',
  Failed = 'failed',
  Rejected = 'rejected',
  Submitted = 'submitted',
  Succeeded = 'succeeded',
}

interface AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType
  time: string
  allocation_id: string
  client_allocation_id: string
}

interface AllocationCreateEvent extends AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType.Create
  order_id: string
  client_order_id: string
  risk_group_id: string
  custodian_id: string
  ib_id: string
  allocation_strategy: string
}

enum AllocationRejectCode {
  Unspecified = 1100,
  RiskGroupNotEnabled = 1101,
  CustodianInvalid = 1102,
  InvalidAllocationRequest = 1103,
}

interface AllocationRejectEvent extends AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType.Reject
  reason: string
  code: AllocationRejectCode
}

interface AllocationApproveEvent extends AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType.Approve
}

enum AllocationCancelCode {
  Unspecified = 1200,
  ApiRequest = 1201,
  OrderCancel = 1202,
  OrderReject = 1203,
}

interface AllocationCancelEvent extends AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType.Cancel
  reason: string
  code: AllocationCancelCode
}

// Note: It is on purpose that this event extends AllocationCreateEvent
interface AllocationCalculateEvent
  extends Omit<AllocationCreateEvent, 'event_type'> {
  event_type: AllocationUpdateEventType.Calculate
  account_allocations: AccountAllocation[]
  trades: Trade[]
}

interface AllocationSubmitEvent extends AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType.Submit
  custodian_id: string
}

enum AllocationFailCode {
  Unspecified = 1000,
}

interface AllocationFailEvent extends AllocationUpdateEventBase {
  event_type: AllocationUpdateEventType.Fail
  reason: string
  code: AllocationFailCode
}

// When client_allocation_id was added to other events, success was already marked as deprecated,
// so the field was intentionally omitted from this event type
// When added back in https://github.com/moment-technology/public-api/pull/1452, update the type to match the other event types
interface AllocationSuccessEvent
  extends Omit<AllocationUpdateEventBase, 'client_allocation_id'> {
  event_type: AllocationUpdateEventType.Succeed
  custodian_id: string
  is_confirmed_by_custodian: boolean
}

export type AllocationUpdateEvent =
  | AllocationCreateEvent
  | AllocationRejectEvent
  | AllocationApproveEvent
  | AllocationCancelEvent
  | AllocationCalculateEvent
  | AllocationSubmitEvent
  | AllocationFailEvent
  | AllocationSuccessEvent
